import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { combineLatest, first, map, Observable, ReplaySubject, take, tap } from 'rxjs';
import { BillingDetails, PublisherDetails, User } from '../models/user.model';

@Injectable({
    providedIn: 'root'
})
export class UserService {

    public readonly BASE_URL = '/publisher/user';
    protected user = new ReplaySubject<User>(1);

    constructor(
        protected http: HttpClient
    ) {
        this.http.get<User>(this.BASE_URL)
            .pipe(take(1))
            .subscribe({
                next: (resp) => this.user.next(resp),
                error: err => this.user.error(err)
            });
    }

    public get user$(): Observable<User> {
        return this.user.asObservable();
    }

    public get clientId$(): Observable<string> {
        return this.user$
            .pipe(
                map(user => user.publisherDetails.userInfo.clientId)
            );
    }

    public get clonedBillingDetails$(): Observable<any> {
        return this.user$
            .pipe(
                map(({ billingDetails }) => {
                    return {
                        ...billingDetails,
                        billingAddress: { ...billingDetails.billingAddress }
                    };
                })
            );
    }

    public get clonedPublisherDetails$(): Observable<any> {
        return this.user$
            .pipe(map(({ publisherDetails: { userInfo, userAddress, settings } }) => {
                return {
                    userInfo: { ...userInfo },
                    userAddress: { ...userAddress },
                    settings: { ...settings }
                };
            }));
    }

    public updatePublisherDetails(publisherDetails: PublisherDetails): Observable<PublisherDetails> {
        const updatedPublisherDetails$ = this.http.post<PublisherDetails>(this.BASE_URL + '/details', publisherDetails);

        return combineLatest([updatedPublisherDetails$, this.user$])
            .pipe(
                first(),
                tap(([updatedPublisherDetails, user]) => {
                    user.publisherDetails = updatedPublisherDetails;
                    this.user.next(user);
                }),
                map(([updatedPublisherDetails]) => updatedPublisherDetails)
            );
    }

    /**
     * ToDo: add types
     * @param billingDetails
     */
    public updateBillingDetails(billingDetails: any): Observable<any> {
        const updatedBillingData$ = this.http.post<BillingDetails>(this.BASE_URL + '/billing-details', billingDetails);

        return combineLatest([updatedBillingData$, this.user$])
            .pipe(
                first(),
                tap(([updatedBillingDetails, user]) => {
                    user.billingDetails = updatedBillingDetails;
                    this.user.next(user);
                }),
                map(([updatedBillingDetails]) => updatedBillingDetails)
            );
    }

    public signOut(): Observable<void> {
        return this.http.get<void>('/signout');
    }
}
